import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Users, TrendingUp, DollarSign } from "lucide-react";

interface TeamMember {
  id: string;
  team_member_id: string;
  status: string;
  joined_at: string;
  team_commission_rate: number;
  total_team_earnings: number;
  profiles?: { username: string };
}

interface TeamStats {
  total_members: number;
  active_members: number;
  total_team_earnings: number;
  total_team_revenue: number;
}

export const TeamManagement = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [stats, setStats] = useState<TeamStats>({
    total_members: 0,
    active_members: 0,
    total_team_earnings: 0,
    total_team_revenue: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load team members
      const { data: membersData } = await supabase
        .from("team_referrals")
        .select("*")
        .eq("team_leader_id", user.id)
        .order("joined_at", { ascending: false });

      if (membersData && membersData.length > 0) {
        // Fetch profiles
        const memberIds = membersData.map(m => m.team_member_id);
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, username")
          .in("id", memberIds);

        const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
        const enriched = membersData.map(m => ({
          ...m,
          profiles: profilesMap.get(m.team_member_id),
        }));

        setTeamMembers(enriched);

        // Calculate stats
        const activeMembers = enriched.filter(m => m.status === "active");
        const totalEarnings = enriched.reduce((sum, m) => sum + m.total_team_earnings, 0);

        setStats({
          total_members: enriched.length,
          active_members: activeMembers.length,
          total_team_earnings: totalEarnings,
          total_team_revenue: totalEarnings * 5, // Estimate based on commission rate
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Team Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Team Size</CardTitle>
            <Users className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_members}</div>
            <p className="text-xs text-muted-foreground">
              {stats.active_members} active members
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Team Earnings</CardTitle>
            <DollarSign className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${(stats.total_team_earnings / 100).toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">
              Your commission from team
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Team Revenue</CardTitle>
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${(stats.total_team_revenue / 100).toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">
              Total revenue generated by team
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Team Members Table */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>
            Earn additional commission from your team's referrals
          </CardDescription>
        </CardHeader>
        <CardContent>
          {teamMembers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No team members yet</p>
              <p className="text-sm mt-2">
                When your referrals start referring others, they'll appear here
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Commission Rate</TableHead>
                  <TableHead>Your Earnings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">
                      {member.profiles?.username || "User"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={member.status === "active" ? "default" : "secondary"}>
                        {member.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(member.joined_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {(member.team_commission_rate * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell>
                      ${(member.total_team_earnings / 100).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
